import { useId, type ReactNode } from "react";
import type { OwlLeg } from "../../owl/useOwlHourSlips.ts";
import { marketLabel } from "../../exchange/market-format.ts";
import { owlTeams, owlKickoff, owlPublicNote, owlSelection } from "./owl-presentation.ts";
export interface OwlLegRowProps {
  leg: OwlLeg;
  index: number;
  isLast: boolean;
  extras?: ReactNode;
}
export function OwlLegRow({ leg, index, extras }: OwlLegRowProps): JSX.Element {
  const id = useId(),
    [home, away] = owlTeams(leg),
    flags = leg.flags ?? {};
  const status = leg.result === "win" ? "Won" : leg.result === "loss" ? "Lost" : "Pending";
  const context = [
    flags.rivalry ? "Rivalry" : null,
    flags.sample_warning ? "Limited data" : null,
    flags.travel_fatigue ? "Travel context" : null,
    flags.altitude ? "Altitude" : null,
    flags.turf ? "Artificial turf" : null,
    flags.home_adv && flags.home_adv > 1 ? "Venue context" : null,
  ].filter(Boolean);
  return (
    <article className="owl-board-leg" aria-labelledby={id}>
      <span className="owl-leg-index">{String(index + 1).padStart(2, "0")}</span>
      <div className="owl-leg-fixture">
        <h3 id={id}>
          {home} <span>vs</span> {away}
        </h3>
        <p>{leg.league || leg.league_short || "Competition unavailable"}</p>
        <time dateTime={leg.kickoff_at}>{owlKickoff(leg.kickoff_at)}</time>
      </div>
      <div className="owl-leg-market" data-label="Market / selection">
        <strong>{owlSelection(leg)}</strong>
        <span>{leg.market ? marketLabel(leg.market) : "Market unavailable"}</span>
      </div>
      <div className="owl-leg-price" data-label="Odds">
        {Number.isFinite(leg.odds) && leg.odds > 1 ? leg.odds.toFixed(2) : "Unpriced"}
      </div>
      <span className={`owl-state-pill owl-leg-state is-${status.toLowerCase()}`}>{status}</span>
      <details className="owl-leg-details">
        <summary>
          Selection details <span aria-hidden="true">+</span>
        </summary>
        <div>
          <p>{owlPublicNote(leg.reasoning)}</p>
          <dl>
            <div>
              <dt>Recorded assessment</dt>
              <dd>{leg.confidence > 0 ? `${leg.confidence}/100` : "Not provided"}</dd>
            </div>
            <div>
              <dt>Match context</dt>
              <dd>{context.length ? context.join(" · ") : "No additional flags recorded"}</dd>
            </div>
          </dl>
          {extras ? <div className="owl-leg-links">{extras}</div> : null}
        </div>
      </details>
    </article>
  );
}
